/* eslint-disable max-nested-callbacks */
// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { Slider } from '@/_shared/components/Slider';
import { Tooltip } from '@/_shared/components/Tooltip';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getUserProfileCall } from '@/core/state/api';
import { postEmployerByIdSurveyResultCall, useSurvey } from '@/surveys/state';
import {
  useContentsLikes,
  ContentLike,
  getContentsLikesCall,
  Content,
} from '../../../../contentsLikes/state';
import { CardItem, COLORS_PLAYGROUND_CARDS } from '../../../state';
import { CourseDetailsDialog } from './CourseDetailsDialog';

type Props = {
  title?: string;
  description?: string;
  elementToView?: {
    desktopLarge?: number;
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
};

export const CardsPlayground: FC<Props> = ({
  title,
  description,
  elementToView = { desktopLarge: 5.5, desktop: 4.5 },
}) => {
  const t = useTranslations('aiPlayground.learning.landingPlayground.courses');

  const [metricsMap, setMetricsMap] = useState<CardItem[]>([]);

  const [parameters, setParameters] = useState<CardItem>();

  const { openDialog: openCourseDetailsDialog } = useDialog('courseDetails');

  const { softSkillGetTemplateId, softSkillGetListMetrics } = useSurvey();

  const onOpenDialog = useCallback(
    (slide: CardItem) => {
      setParameters({
        title: slide.title,
        totalInterestedPeople: slide.totalInterestedPeople,
        longDescription: slide.longDescription,
        shortDescription: slide.shortDescription,
        color1: slide.color1,
        color2: slide.color2,
        linkBrochure: slide.linkBrochure,
        hasInterestBadge: slide.hasInterestBadge,
        contentHash: slide.contentHash,
      });
      openCourseDetailsDialog();
    },
    [openCourseDetailsDialog],
  );

  const {
    execute: executePostEmployerByIdSurveyResult,
    response: responsePostEmployerByIdSurveyResult,
  } = postEmployerByIdSurveyResultCall.hook();
  const {
    execute: executeGetContentsLikes,
    response: responseGetContentsLikes = [],
    success: successGetContentsLikes,
  } = getContentsLikesCall.hook();

  const { insertContentLike, removeContentLike } = useContentsLikes();

  const onClickLike = useCallback(
    (metric: string, currentContentHash: string) => {
      if (currentContentHash !== '') {
        removeContentLike(
          currentContentHash,
          () => {
            setMetricsMap(prevMetrics => {
              return prevMetrics.map(metricItem => {
                if (metricItem.title === metric) {
                  return {
                    ...metricItem,
                    hasInterestBadge: false,
                    contentHash: '',
                  };
                }
                return metricItem;
              });
            });
          },
          true,
        );
      } else {
        insertContentLike(
          {
            contentType: ContentLike.AI_PLAYGROUND_CARD,
            metadata: {
              metric,
            },
          },
          contentHash => {
            setMetricsMap(prevMetrics => {
              return prevMetrics.map(metricItem => {
                if (metricItem.title === metric) {
                  return {
                    ...metricItem,
                    hasInterestBadge: true,
                    contentHash,
                  };
                }
                return metricItem;
              });
            });
          },
          true,
        );
      }
    },
    [insertContentLike, removeContentLike],
  );

  const data = useMemo(() => {
    const getResults = responsePostEmployerByIdSurveyResult?.find(
      r => r.templateId === softSkillGetTemplateId,
    );
    const canViewResults = responsePostEmployerByIdSurveyResult?.find(
      r => r.templateId === softSkillGetTemplateId,
    )?.canViewResults;

    const internalMap = softSkillGetListMetrics.map((metric, index) => {
      const { color1, color2 } = COLORS_PLAYGROUND_CARDS[index] || {
        color1: 'rgba(1, 253, 236, 1)',
        color2: 'rgba(27, 33, 41, 1)',
      };
      const currentDataFromBackend = responseGetContentsLikes.find(
        (interaction: { content?: Content; contentHash?: string }) => {
          if (!interaction.content) {
            return false;
          }
          return (
            interaction.content.contentType === ContentLike.AI_PLAYGROUND_CARD &&
            interaction.content.metadata.metric === metric.name
          );
        },
      ) as { contentHash: string } | undefined;
      return {
        title: metric.name || '',
        color1,
        color2,
        shortDescription: metric.shortDescription || '',
        longDescription: metric.longDescription || '',
        hasInterestBadge: !!currentDataFromBackend,
        skillToBoost: false,
        contentHash: currentDataFromBackend ? currentDataFromBackend?.contentHash : '',
        score:
          getResults?.results?.results?.find(r => r.metric?.name === metric.name)?.score ??
          undefined,
      };
    });

    return internalMap
      .sort((a, b) => (a.score ?? Infinity) - (b.score ?? Infinity))
      .map((item, index) => ({
        ...item,
        skillToBoost: index < 2 && canViewResults && item.score !== undefined && item.score >= 0,
      }));
  }, [
    responsePostEmployerByIdSurveyResult,
    softSkillGetListMetrics,
    softSkillGetTemplateId,
    responseGetContentsLikes,
  ]);

  const { email } = getUserProfileCall.hook();

  useEffect(() => {
    executePostEmployerByIdSurveyResult({
      username: email,
    });
    executeGetContentsLikes({
      contentType: ContentLike.AI_PLAYGROUND_CARD,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (successGetContentsLikes && softSkillGetListMetrics.length > 0) {
      setMetricsMap(data);
    }
  }, [data, softSkillGetListMetrics.length, successGetContentsLikes]);

  if (metricsMap.length === 0) {
    return null;
  }

  return (
    <>
      <Box mt='s'>
        <Box mb='xxs' display='flex' flexDirection='column'>
          {title === undefined ? (
            <Box alignItems='center' display='flex' flexDirection='row'>
              <Typography variant='h2'>{t('title')}</Typography>
              <Tooltip
                iconProps={{ size: 'xs', fill: 'grey/light' }}
                containerProps={{ ml: 'xxs' }}
              >
                {t('tooltip')}
              </Tooltip>
            </Box>
          ) : (
            <Typography variant='h2'>{title}</Typography>
          )}
          {description && (
            <Typography mt='xxxs' mb='xs' variant='p3/light'>
              {description}
            </Typography>
          )}
        </Box>
        <Box>
          <Slider.SliderBottomRightArrow
            slideToShow={{
              desktopLarge: elementToView?.desktopLarge || undefined,
              desktop: elementToView?.desktop || undefined,
              tablet: elementToView?.tablet || undefined,
              mobile: elementToView?.mobile || undefined,
            }}
          >
            {metricsMap.map(slide => {
              return (
                <Card p='xxxs' minHeight='200px' key={slide.title}>
                  <Box
                    background={`conic-gradient(from 120deg at 25% 40%, ${slide.color2} 0deg, ${slide.color1} 300deg)`}
                    borderRadius='l'
                    height={200}
                    display='flex'
                    flexDirection='column'
                    justifyContent='space-between'
                  >
                    <Box
                      display='flex'
                      flexDirection='row'
                      justifyContent='space-between'
                      alignItems='center'
                      ml='xs'
                      mr='xs'
                      mt='xs'
                    >
                      {slide.skillToBoost ? (
                        <Box
                          px='xxs'
                          borderRadius='xl'
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          }}
                        >
                          <Typography color='white' variant='p3/label'>
                            {t('card.skillToBoost')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box />
                      )}
                      <Box
                        display='flex'
                        alignItems='center'
                        px='xxs'
                        py='xxs'
                        borderRadius='xl'
                        backgroundColor='white'
                        onClick={() => onClickLike(slide.title, slide.contentHash || '')}
                        style={{ cursor: 'pointer' }}
                      >
                        {slide.hasInterestBadge ? (
                          <Svg name='like' fill='primary' size='xs' />
                        ) : (
                          <Svg name='likeEmpty' fill='primary' size='xs' />
                        )}
                      </Box>
                    </Box>
                    <Typography mb='xs' ml='xs' variant='h2'>
                      {slide.title}
                    </Typography>
                  </Box>
                  <Box p='xxs'>
                    <Typography variant='p3/light' minHeight='80px'>
                      {slide.shortDescription}
                    </Typography>
                    <Button onClick={() => onOpenDialog(slide)} mt='s' variant='positive'>
                      {t('card.buttonLabel')}
                    </Button>
                  </Box>
                </Card>
              );
            })}
          </Slider.SliderBottomRightArrow>
        </Box>
      </Box>
      {parameters && <CourseDetailsDialog setMetricsMap={setMetricsMap} item={parameters} />}
    </>
  );
};
